/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '@mui/material';
import { SelectionMode } from '@grapecity/wijmo.grid';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import { toggleClass } from '@grapecity/wijmo';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { getExcelFileName } from 'utils/ExcelUtil';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { useNavigate } from 'react-router-dom';
import { Code } from 'models/common/CommonCode';
import { CellType, DataMap } from '@grapecity/wijmo.grid';
import { hasRole } from 'utils/SessionUtil';
import { CrudCode } from 'models/common/Edit';
import useEvent from 'react-use-event-hook';
import { FlexGridFilter, ColumnFilter } from '@grapecity/wijmo.grid.filter';

import _ from 'lodash';
import { SearchParam, UtMatrixStandardCode } from 'models/ut/UtMatrixStandardCode';
import {
  checkDuplicationStandardCode,
  getUtMatrixStandardCodeList,
  saveUtMatrixStandardCodeList,
} from 'apis/ut/UtMatrixStandartdCode';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { SuccessOrNot } from '../../models/common/RestApi';
import UtMatrixRegistHistoryPoupup from './popup/UtMatrixRegistHistoryPoupup';
import UtMatrixEumTagIdPopup from './popup/UtMatrixEumTagIdPopup';

/**
 * @description UT Matrix  - 표준설비코드
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.09.24  sungim.kim2     [고도화] EUM TAG 컬럼 추가
 *                             [고도화] 설비별 UT Matrix 팝업 추가
 */
const UtMatrixStandardCodePage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const gridRef = useRef<any>();
  const [hitTest, setHitTest] = useState<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [flexItem, setFlexItem] = useState<any>();

  const [rowData, setRowData] = useState<UtMatrixStandardCode[]>([]);

  // [고도화] 설비별 UT Matrix 팝업 추가
  const [utMatrixRegistHistoryCondition, setUtMatrixRegistHistoryCondition] = useState<any>();
  const [isUtMatrixRegistHistoryModalOpen, setUtMatrixRegistHistoryModalOpen] =
    useState<boolean>(false);

  const [isUtMatrixEumTagIdPopUpOpen, setUtMatrixEumTagIdPopUpOpen] = useState<boolean>(false);

  const [getCommPrdnProcCode, setCommPrdnProcCode] = useState<Code[]>([]); // 공정 공정코드
  const [getCommEqclIdCodeList, setCommEqclIdCodeList] = useState<Code[]>([]); // 설비군 공통코드
  const [getCommDtalProcCodeList, setCommDtalProcCodeList] = useState<Code[]>([]); // 세부공정 공통코드

  const [eqclIdCode, setEqclIdCode] = useState<Code[]>([]); // 설비군 조회조건
  const [dtalProcCode, setDtalProcCode] = useState<Code[]>([]); // 세부공정 조회조건

  const [dtalProcListCode, setDtalProcListCode] = useState<Code[]>([]); // 공정-설비군-세부공정 공통코드(하이라키)

  const [changeCode, setChangeCode] = useState<string>('');

  const initSearchParam = {
    prdnProcCds: [],
    eqclIds: [],
    dtalProcCds: [],
    eqpMchId: '',
  };

  const [searchParam, setSearchParam] = useState<SearchParam>(initSearchParam);

  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const [delData, setDelData] = useState<UtMatrixStandardCode[]>([]);

  const isReadOnly = useMemo(() => {
    return hasAuth;
  }, [hasAuth]);

  useEffect(() => {
    initCondition();
    if (hasRole('ADM') || hasRole('UT_MANAGER')) {
      setHasAuth(true);
    }
    btnSearch();
  }, []);

  const initCondition = async () => {
    const prdnProcCd = await getCommonCodeNamesCondition({
      optValCtn1: 'Y',
      cmnGrCd: 'PRDN_PROC_CD',
    });
    const eqclIdCd = await getCommonCodeNamesCondition({
      optValCtn1: 'Y',
      cmnGrCd: 'EQCL_ID',
    });
    const dtalProcCd = await getCommonCodeNames('DTAL_PROC_CD');

    setCommPrdnProcCode(prdnProcCd);
    setCommEqclIdCodeList(eqclIdCd);
    setCommDtalProcCodeList(dtalProcCd);

    const dtalProclist = await getCommonCodeNames('DTAL_PROC_LIST'); //공정-설비군-세부공정 하이라키 구조 공통코드
    setDtalProcListCode(dtalProclist); //공정-설비군-세부공정 하이라키 구조 공통코드
  };

  useEffect(() => {
    if (changeCode === 'prdnProcCd') {
      searchParam.eqclIds = [];
      searchParam.dtalProcCds = [];
      const selectedPrdnProcCd = searchParam.prdnProcCd?.split(',') || [];

      const findedEqclIdList: Code[] = [];
      if (selectedPrdnProcCd?.length > 0) {
        for (let i = 0; i < selectedPrdnProcCd.length; i++) {
          const selectedCode: Code[] = dtalProcListCode.filter(
            (code) => code.optValCtn1 === selectedPrdnProcCd[i]
          );
          findedEqclIdList.push(...selectedCode);
        }
      }

      const makeEqclIdCode: Code[] = [];
      for (let i = 0; i < findedEqclIdList.length; i++) {
        for (let j = 0; j < getCommEqclIdCodeList.length; j++) {
          if (findedEqclIdList[i].optValCtn2 === getCommEqclIdCodeList[j].cmnCd) {
            makeEqclIdCode.push({
              cmnCd: findedEqclIdList[i].optValCtn2,
              cmnCdNm: getCommEqclIdCodeList[j].cmnCdNm,
            });
          }
        }
      }

      const deleteDuplication = makeEqclIdCode.filter((code, index) => {
        return (
          makeEqclIdCode.findIndex((scdCode) => {
            return code.cmnCd === scdCode.cmnCd;
          }) === index
        );
      });

      setEqclIdCode(deleteDuplication);
    }

    if (changeCode === 'eqclId') {
      searchParam.dtalProcCds = [];
      const selectedEqclId = searchParam.eqclId?.split(',') || [];

      const findedDtalProcCdList: Code[] = [];
      if (selectedEqclId?.length > 0) {
        for (let i = 0; i < selectedEqclId.length; i++) {
          const selectedCode: Code[] = dtalProcListCode.filter(
            (code) => code.optValCtn2 === selectedEqclId[i]
          );
          findedDtalProcCdList.push(...selectedCode);
        }
      }

      const makeDtalProcCode: Code[] = [];
      for (let i = 0; i < findedDtalProcCdList.length; i++) {
        for (let j = 0; j < getCommDtalProcCodeList.length; j++) {
          if (findedDtalProcCdList[i].optValCtn3 === getCommDtalProcCodeList[j].cmnCd) {
            makeDtalProcCode.push({
              cmnCd: findedDtalProcCdList[i].optValCtn3,
              cmnCdNm: getCommDtalProcCodeList[j].cmnCdNm,
            });
          }
        }
      }

      const deleteDuplication = makeDtalProcCode.filter((code, index) => {
        return (
          makeDtalProcCode.findIndex((scdCode) => {
            return code.cmnCd === scdCode.cmnCd;
          }) === index
        );
      });

      setDtalProcCode(deleteDuplication);
    }
  }, [searchParam]);

  const layoutDefinition = useMemo(() => {
    const prdnProcCodeMap = new DataMap(getCommPrdnProcCode, 'cmnCd', 'cmnCdNm');
    const eqpClIdCodeMap = new DataMap(getCommEqclIdCodeList, 'cmnCd', 'cmnCdNm');
    const dtalProcCodeMap = new DataMap(getCommDtalProcCodeList, 'cmnCd', 'cmnCd');
    const dtalProcCodeNmMap = new DataMap(getCommDtalProcCodeList, 'cmnCd', 'cmnCdNm');
    return [
      // {
      //   binding: 'no',
      //   header: String(t('com.label.NO', 'NO')),
      //   width: 40,
      //   isReadOnly: true,
      //   align: 'center',
      //   cellTemplate: (grid) => grid.row._idx + 1,
      // },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        width: 43,
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'prdnProcCd',
        header: String(t('ut.label.공정', '공정')),
        width: 110,
        isReadOnly: !hasAuth,
        cssClass: 'WijmoSelect',
        dataMap: prdnProcCodeMap,
        cellTemplate: (item) => {
          if (item.item.crudKey !== null) {
            if (prdnProcCodeMap.getDisplayValue(item.value) == undefined) {
              return `<span>${item.text}</span>`;
            } else {
              return `<span>${prdnProcCodeMap.getDisplayValue(item.text)}</span>`;
            }
          } else {
            return `<span>${item.item.prdnProcCdNm}</span>`;
          }
        },
      },
      {
        binding: 'prdnProcCdNm',
        isReadOnly: true,
        visible: false,
      },
      {
        binding: 'eqclId',
        header: String(t('ut.label.설비군명', '설비군명')),
        width: 90,
        isReadOnly: !hasAuth,
        cssClass: 'WijmoSelect',
        dataMap: eqpClIdCodeMap,
        cellTemplate: (item) => {
          if (item.item.crudKey !== null) {
            return `<span>${
              item.item.eqclId === undefined ? '' : eqpClIdCodeMap.getDisplayValue(item.item.eqclId)
            }</span>`;
          } else {
            return `<span>${eqpClIdCodeMap.getDisplayValue(item.item.eqclId)}</span>`;
          }
        },
      },
      {
        binding: 'eqclIdNm',
        header: String(t('ut.label.설비군명', '설비군명')),
        visible: false,
      },
      {
        binding: 'dtalProcCd',
        header: String(t('ut.label.세부공정코드', '세부공정코드')),
        width: 120,
        align: 'center',
        isReadOnly: !hasAuth,
        cssClass: 'WijmoSelect',
        dataMap: dtalProcCodeMap,
        cellTemplate: (item) => {
          if (item.item.crudKey !== null) {
            return `<span>${item.item.dtalProcCd === undefined ? '' : item.item.dtalProcCd}</span>`;
          } else {
            return `<span>${item.value}</span>`;
          }
        },
      },
      {
        binding: 'dtalProcCdNm',
        header: String(t('ut.label.세부공정명', '세부공정명')),
        width: 105,
        isReadOnly: true,
        cellTemplate: (item) => {
          if (item.item.crudKey !== null) {
            return `<span>${
              item.item.dtalProcCd === undefined
                ? ''
                : dtalProcCodeNmMap.getDisplayValue(item.item.dtalProcCd)
            }</span>`;
          } else {
            return `<span>${item.value}</span>`;
          }
        },
      },
      {
        binding: 'eqpMainId',
        header: String(t('ut.label.Main ID', 'Main ID')),
        width: 87,
        align: 'center',
        isReadOnly: !hasAuth,
        // cssClass: 'WijmoSelect',
        // dataMap: electrodeCodeMap,
        // cellTemplate: (item) => {
        //   if (item.item.crudKey === CrudCode.CREATE) {
        //     return `<span>${item.item.eqpMainId === undefined ? '' : item.item.eqpMainId}</span>`;
        //   }
        // },
      },
      {
        binding: 'eqpMainNm',
        header: String(t('ut.label.Main 명', 'Main 명')),
        width: 125,
        isReadOnly: !hasAuth,
        // cellTemplate: (item) => {
        //   if (item.item.crudKey === CrudCode.CREATE) {
        //     return `<span>${
        //       item.item.eqpMainId === undefined
        //         ? ''
        //         : electrodeCodeNmMap.getDisplayValue(item.item.eqpMainId)
        //     }</span>`;
        //   }
        // },
      },
      {
        binding: 'eqpMchId',
        header: String(t('ut.label.Machine ID', 'Machine ID')),
        width: 120,
        isReadOnly: !hasAuth,
      },
      {
        binding: 'eqpMchNm',
        header: String(t('ut.label.Machine 명', 'Machine 명')),
        width: 180,
        isReadOnly: !hasAuth,
      },
      {
        binding: 'stndEqpId',
        header: String(t('ut.label.표준설비코드', '표준설비코드')),
        width: 150,
        align: 'center',
        isReadOnly: true,
        cellTemplate: (item) => {
          if (item.item.crudKey === CrudCode.CREATE) {
            if (item.item.dtalProcCd && item.item.eqpMainId && item.item.eqpMchId) {
              return `<span>${(item.item.stndEqpId =
                item.item.dtalProcCd + '-' + item.item.eqpMainId + '-' + item.item.eqpMchId)}
              </span>`;
            }
          } else {
            return `<span>${item.value}</span>`;
          }
        },
      },
      {
        binding: 'libraryQty',
        header: String(t('ut.label.Library 수', 'Library 수')),
        width: 99,
        isReadOnly: true,
        align: 'center',
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            fnSearchLibrary(ctx.item);
          },
        }),
      },
      // [고도화] EUM TAG 정보 추가
      {
        binding: 'tagNm',
        header: String(t('ut.label.EUM Tag명', 'EUM Tag명')),
        width: '*',
        isReadOnly: true,
        align: 'left',
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.등록자', '등록자')),
        width: 80,
        isReadOnly: true,
        align: 'center',
        visible: false,
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.등록일', '등록일')),
        width: 100,
        isReadOnly: true,
        align: 'center',
        visible: false,
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        width: 80,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일', '수정일')),
        width: 100,
        isReadOnly: true,
        align: 'center',
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);
      grid.startEditing(true);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.

      const item = grid.rows[ht.row].dataItem; // 선택한 row의 정보

      if (e.target instanceof HTMLButtonElement) {
        const col = grid.columns[ht.col]; // 선택한 컬럼의 정보
        const item = grid.rows[ht.row].dataItem; // 선택한 row의 정
        setFlexItem(item);
        setHitTest(ht);
        switch (e.target.id) {
          case 'gridWrtUserId':
            break;
          case 'gridReadWrtUserId':
            break;
        }
      }
    });
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (item.crudKey !== CrudCode.CREATE) {
        if (['prdnProcCd', 'eqclId', 'dtalProcCd', 'eqpMainId', 'eqpMchId'].includes(binding)) {
          cell.ariaReadOnly = true;
          toggleClass(cell, 'WijmoSelect', !isReadOnly);
          if (isReadOnly && cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const beginningEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;

    if (item.crudKey !== CrudCode.CREATE) {
      if (['prdnProcCd', 'eqclId', 'dtalProcCd', 'eqpMainId', 'eqpMchId'].includes(binding)) {
        e.cancel = true;
      }
    }

    const selectedEqclId: Code[] = [];
    if (binding === 'prdnProcCd' && item.crudKey === CrudCode.CREATE) {
      // eqclId/dtalProcCd 초기화는 cellEditEnding에서 값 변경 시에만 처리
    }
    if (binding === 'eqclId' && item.crudKey === CrudCode.CREATE) {
      // dtalProcCd 초기화는 cellEditEnding에서 값 변경 시에만 처리
      const selectedCode = dtalProcListCode.filter((code) => code.optValCtn1 === item.prdnProcCd);
      const result: Code[] = [];
      for (let i = 0; i < selectedCode.length; i++) {
        for (let j = 0; j < getCommEqclIdCodeList.length; j++) {
          if (selectedCode[i].optValCtn2 === getCommEqclIdCodeList[j].cmnCd) {
            result.push({
              cmnCd: selectedCode[i].optValCtn2,
              cmnCdNm: getCommEqclIdCodeList[j].cmnCdNm,
            });
          }
        }
      }
      const deleteDuplication = result.filter((code, index) => {
        return (
          result.findIndex((scdCode) => {
            return code.cmnCd === scdCode.cmnCd;
          }) === index
        );
      });
      col.dataMap = new DataMap(deleteDuplication, 'cmnCd', 'cmnCdNm');
      selectedEqclId.concat(deleteDuplication);
    }

    if (binding === 'dtalProcCd') {
      const selectedPrdnCode = dtalProcListCode.filter(
        (code) => code.optValCtn1 === item.prdnProcCd
      );
      const selectedEqclIdCode = selectedPrdnCode.filter((code) => code.optValCtn2 === item.eqclId);
      const result: Code[] = [];
      for (let i = 0; i < selectedEqclIdCode.length; i++) {
        for (let j = 0; j < getCommDtalProcCodeList.length; j++) {
          if (selectedEqclIdCode[i].optValCtn3 === getCommDtalProcCodeList[j].cmnCd) {
            result.push({
              cmnCd: selectedEqclIdCode[i].optValCtn3,
              cmnCdNm: getCommDtalProcCodeList[j].cmnCdNm,
            });
          }
        }
      }
      col.dataMap = new DataMap(result, 'cmnCd', 'cmnCd');
    }
  });

  const cellEditEnding = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;

    if (item.crudKey !== CrudCode.CREATE) return;

    const oldVal = grid.getCellData(e.row, e.col, true);
    const newVal = grid.activeEditor?.value;

    if (binding === 'prdnProcCd' && oldVal !== newVal) {
      item.eqclId = '';
      item.dtalProcCd = '';
    }

    if (binding === 'eqclId' && oldVal !== newVal) {
      item.dtalProcCd = '';
    }
  });

  const editEnded = useEvent((grid, e) => {
    if (grid.rows == 0) return;
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
    if (binding === 'dtalProcCd') {
      if (!_.isEmpty(item.dtalProcCd)) {
        const dtalSelectedCode = getCommDtalProcCodeList.filter(
          (dtalCode) => dtalCode.cmnCd === item.dtalProcCd
        );
        item.dtalProcCdNm = dtalSelectedCode[0].cmnCdNm;
      }
    }
    if (binding === 'prdnProcCd' || binding === 'eqpMainId' || binding === 'eqpMchId') {
      if (!_.isEmpty(item.dtalProcCd) && !_.isEmpty(item.eqpMainId) && !_.isEmpty(item.eqpMchId)) {
        item.stndEqpId = item.dtalProcCd + '-' + item.eqpMainId + '-' + item.eqpMchId;
      }
    }
  });

  const handleOnChange = (name, value) => {
    setSearchParam({ ...searchParam, [name]: value });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = 'Ut Matrix 대상 기준정보';
    book.saveAsync(
      getExcelFileName(t('ut.label.UT Matrix 대상 기준정보', 'UT Matrix 대상 기준정보'))
    );
  };

  const btnSearch = () => {
    getUtMatrixStandardCodeList(searchParam).then((result: UtMatrixStandardCode[]) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnReload = () => {
    setSearchParam(
      (prev) =>
        Object.keys(prev).reduce(
          (acc, cur) => Object.assign(acc, { [cur]: initSearchParam[cur] || '' }),
          {}
        ) as SearchParam
    );
    setEqclIdCode([]);
    setDtalProcCode([]);
  };

  const btnAddRow = () => {
    if (rowData.length === 0) {
      const newRow = {
        crudKey: CrudCode.CREATE,
        useYn: 'Y',
      } as UtMatrixStandardCode;
      setRowData([newRow, ...rowData]);
    }
    const newRows = [] as UtMatrixStandardCode[];
    newRows.push({
      crudKey: CrudCode.CREATE,
      useYn: 'Y',
    });
    gridRef.current.collectionView.sourceCollection.unshift(...newRows);
    gridRef.current.collectionView.refresh();
  };

  const btnDelRow = () => {
    if (flexRef === null) {
      openMessageBar({
        type: 'error',
        content: t('ut.label.기준정보를 선택해 주세요.', '기준정보를 선택해 주세요.'),
      });
      return false;
    }

    const selectedRowNodes = flexRef.selectedRows;

    const isSelected = selectedRowNodes.filter((item) => item.isSelected);
    const selectedData = isSelected.map(({ dataItem }) => dataItem);

    const valid = selectedData
      .map((item) => {
        if (item.libraryQty !== null && item.crudKey !== CrudCode.CREATE) {
          openMessageBar({
            type: 'error',
            content:
              item.stndEqpId +
              ': ' +
              t(
                'ut.label.사용중인 기준정보 입니다. 삭제할 수 없습니다.',
                '사용중인 기준정보 입니다. 삭제할 수 없습니다.'
              ),
          });
          return false;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) return false;

    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('ut.label.기준정보를 선택해 주세요.', '기준정보를 선택해 주세요.'),
      });
      return false;
    } else {
      const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
      if (selectedRows.length < 1) return;

      selectedRows.forEach((row) => {
        const item = row.dataItem;
        if (item.crudKey === CrudCode.CREATE) {
          gridRef.current.collectionView.remove(item);
        } else {
          item.crudKey = CrudCode.DELETE;
        }
      });
      gridRef.current.refresh();
      // const selectedIds = selectedRowNodes
      //   .map((rowNode) => {
      //     return parseInt(rowNode._idx!);
      //   })
      //   .reverse();

      // selectedIds.forEach((idx) => {
      //   if (rowData[idx].crudKey === CrudCode.CREATE) {
      //     delete rowData[idx];
      //   } else {
      //     rowData[idx].crudKey = CrudCode.DELETE;
      //   }
      // });

      // const filteredData = rowData.filter((element) => element !== undefined);
      // setRowData(filteredData);
    }
  };

  const btnCopyRow = () => {
    if (flexRef == null) {
      openMessageBar({
        type: 'error',
        content: t('ut.label.기준정보를 선택해 주세요.', '기준정보를 선택해 주세요.'),
      });
      return false;
    }

    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    const filteredData = gridRef.current.rows.map(({ dataItem }) => dataItem);

    // const isSelected = selectedRowNodes.filter((item) => item.isSelected);

    if (selectedRows.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('ut.label.기준정보를 선택해 주세요.', '기준정보를 선택해 주세요.'),
      });
      return false;
    } else {
      const newRows = [] as UtMatrixStandardCode[];
      selectedRows.forEach((o) => {
        const item = o.dataItem;
        newRows.push({
          // ...item,
          crudKey: CrudCode.CREATE,
          prdnProcCd: item?.prdnProcCd,
          eqclId: item?.eqclId,
          dtalProcCd: item?.dtalProcCd,
          dtalProcCdNm: item?.dtalProcCdNm,
          eqpMainId: item?.eqpMainId,
          eqpMainNm: item?.eqpMainNm,
          useYn: item?.useYn,
        });
      });
      gridRef.current.collectionView.sourceCollection.unshift(...newRows);
    }
    gridRef.current.collectionView.refresh();
  };

  const btnSave = () => {
    const validStatusCorU = rowData.filter(
      (element) => element.crudKey == CrudCode.CREATE || element.crudKey == CrudCode.UPDATE
    );
    const valid = validStatusCorU
      .map((rowNode, index) => {
        if (rowNode.crudKey != CrudCode.DELETE) {
          if (rowNode.prdnProcCd == null || rowNode.prdnProcCd == '')
            return `${t('ut.label.공정을 선택해 주세요.', '공정을 선택해 주세요.')}`;
          if (rowNode.eqclId == null || rowNode.eqclId == '')
            return `${t('ut.label.설비군명을 선택해 주세요.', '설비군명을 선택해 주세요.')}`;
          if (rowNode.dtalProcCd == null || rowNode.dtalProcCd == '')
            return `${t(
              'ut.label.세부공정코드를 선택해 주세요.',
              '세부공정코드를 선택해 주세요.'
            )}`;
          if (rowNode.dtalProcCdNm == null || rowNode.dtalProcCdNm == '')
            return `${t('ut.label.세부공정명을 입력해 주세요.', '세부공정명을 입력해 주세요.')}`;
          if (rowNode.eqpMainId == null || rowNode.eqpMainId == '')
            return `${t('ut.label.Main ID를 입력해 주세요.', 'Main ID를 입력해 주세요.')}`;
          if (rowNode.eqpMainNm == null || rowNode.eqpMainNm == '')
            return `${t('ut.label.Main 명을 입력해 주세요.', 'Main 명을 입력해 주세요.')}`;
          if (rowNode.eqpMchId == null || rowNode.eqpMchId == '')
            return `${t('ut.label.Machine ID를 입력해 주세요.', 'Machine ID를 입력해 주세요.')}`;
          if (rowNode.eqpMchNm == null || rowNode.eqpMchNm == '')
            return `${t('ut.label.Machine 명을 입력해 주세요.', 'Machine 명을 입력해 주세요.')}`;
          if (rowNode.stndEqpId == null || rowNode.stndEqpId == '')
            return `${t('ut.label.표준설비코드가 없습니다.', '표준설비코드가 없습니다.')}`;
          // if (rowNode.crudKey !== CrudCode.CREATE)
          //   return `${t(
          //     'ut.label.사용중인 기준정보 입니다. 삭제할 수 없습니다.',
          //     '사용중인 기준정보 입니다. 삭제할 수 없습니다.'
          //   )}`;
        }
      })
      .filter((element) => element !== undefined);
    if (valid.length) {
      const content = valid[0]?.toString();
      openMessageBar({ type: 'error', content: content || '' });
      return false;
    }

    // 중복 체크
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        const saveRows = rowData
          .map((rowNode) => {
            return rowNode.crudKey ? rowNode : null;
          })
          .filter((element) => element !== null) as UtMatrixStandardCode[];

        const saveRowsData = [...saveRows, ...delData];
        checkDuplicationStandardCode(saveRowsData).then((result) => {
          if (result) {
            openMessageBar({
              type: 'error',
              content:
                t(
                  'com.label.기 등록된 표준설비코드가 존재합니다.',
                  '기 등록된 표준설비코드가 존재합니다.'
                ) +
                ' ' +
                result,
            });
            return false;
          } else {
            saveUtMatrixStandardCodeList(saveRowsData).then((result) => {
              if (!result || result.successOrNot === SuccessOrNot.N) {
                openMessageBar({
                  type: 'error',
                  content:
                    result.data ||
                    t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
                });
                return false;
              } else {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.label.저장되었습니다.', '저장되었습니다.'),
                });
                setDelData([]);
                btnSearch();
              }
            });
          }
        });
      },
      noCallback: () => {
        return false;
      },
    });
  };

  const fnSearchLibrary = (params: any) => {
    const searchParam = {
      prdnProcCds: params.prdnProcCd,
      eqclIds: params.eqclId,
      dtalProcCds: params.dtalProcCd,
      eqpMchId: params.eqpMchId,
      stndEqpId: params.stndEqpId,
    };
    navigate('/ut/info-management/library', { state: searchParam });
  };

  return (
    <ContainerLayout>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('ut.label.공정', '공정'))}</label>
                <MultiComboBox
                  id="prdnProcCd"
                  options={getCommPrdnProcCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.prdnProcCds || []}
                  onChange={(value) => {
                    handleOnChange('prdnProcCd', value.join());
                    setChangeCode('prdnProcCd');
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('ut.label.설비군', '설비군'))}</label>
                <MultiComboBox
                  id="eqpGrcd"
                  options={eqclIdCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.eqclIds || []}
                  onChange={(value) => {
                    handleOnChange('eqclId', value.join());
                    setChangeCode('eqclId');
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('ut.label.세부공정', '세부공정'))}</label>
                <MultiComboBox
                  id="dtalProcCode"
                  options={dtalProcCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.dtalProcCds || []}
                  onChange={(value) => {
                    handleOnChange('dtalProcCd', value.join());
                    setChangeCode('dtalProcCd');
                  }}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('com.label.Machine', 'Machine')}</label>
                <CustomInputWithSearch
                  name="eqpMchNm"
                  value={searchParam.eqpMchNm || ''}
                  defaultValue={searchParam.eqpMchNm || ''}
                  placeholder={String(
                    t('com.label.Machine을 입력해 주세요.', 'Machine을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.표준설비코드', '표준설비코드')}</label>
                <CustomInputWithSearch
                  name="stndEqpId"
                  value={searchParam?.stndEqpId || ''}
                  defaultValue={searchParam?.stndEqpId || ''}
                  placeholder={String(
                    t('com.label.표준설비코드를 입력해 주세요.', '표준설비코드를 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button
              css={IconButton.button}
              className="reload"
              onClick={btnReload}
              disableRipple
            ></Button>
            <Button css={IconButton.button} className="find" onClick={btnSearch} disableRipple>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.UT Matrix 대상 기준정보', 'UT Matrix 대상 기준정보')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <Button
              css={IconButton.button}
              className="regist"
              onClick={() => setUtMatrixEumTagIdPopUpOpen(true)}
              disableRipple
            >
              {t('com.button.EUM TAG 매핑', 'EUM TAG 매핑')}
            </Button>
          )}
          <Button
            css={IconButton.button}
            className="info"
            onClick={() => {
              // const items = gridRef.current.rows.filter((r) => r.isSelected).map((o) => o.dataItem);
              // if (items.length > 1) {
              //   openMessageBar({
              //     type: 'error',
              //     content: t(
              //       'ut.label.검색할 대상을 1건만 선택해 주세요.',
              //       '검색할 대상을 1건만 선택해 주세요.'
              //     ),
              //   });
              //   return;
              // }
              // setUtMatrixRegistHistoryCondition({
              //   stndEqpId: items.length < 1 ? '' : items[0].stndEqpId,
              // });
              setUtMatrixRegistHistoryModalOpen(true);
            }}
          >
            {t('ut.label.설비별 UT Matrix', '설비별 UT Matrix')}
          </Button>
          {hasAuth && (
            <Button css={IconButton.button} className="addRow" onClick={btnAddRow} disableRipple>
              {t('com.button.행추가', '행추가')}
            </Button>
          )}
          {hasAuth && (
            <Button css={IconButton.button} className="delRow" onClick={btnDelRow} disableRipple>
              {t('com.button.행삭제', '행삭제')}
            </Button>
          )}
          {hasAuth && (
            <Button css={IconButton.button} className="copyRow" onClick={btnCopyRow} disableRipple>
              {t('com.button.행복사', '행복사')}
            </Button>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          {/* <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => setOpenPopup(true)}
            disableRipple
          >
            {t('com.button.표준설비코드팝업', '표준설비코드팝업')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => setOpenPopup2(true)}
            disableRipple
          >
            {t('com.button.작성내역팝업', '작성내역팝업')}
          </Button> */}
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        height={575}
        beginningEdit={beginningEdit}
        cellEditEnding={cellEditEnding}
        cellEditEnded={editEnded}
        itemFormatter={onItemFormatter}
        allowPinning={false}
        selectionMode={SelectionMode.Row}
        excludePin={[
          'prdnProcCdNm',
          'eqclIdNm',
          'stndEqpId',
          'dtalProcCdNm',
          'eqpMainNm',
          'eqpMchNm',
          'utmLibId',
          'utmLibVerNo',
          'prjNm',
          'vdcpNm',
          'genNm',
          'vltgNvl',
          'elpwPhasCd',
          'ctwTpCd',
          'od2EcNvl',
          'brkeCapa',
          'elpwCapa',
          'elpwEumMaxNvl',
          'cdaCspCapa',
          'cdaEumMaxNvl',
          'nitrPeakCspCapa',
          'iwCspCapa',
          'frwtCspCapa',
          'coolCspCapa',
          'wwCspCapa',
          'stemCspCapa',
          'lngCspCapa',
          'insdExhaCspCapa',
          'otsdExhaCspCapa',
          'vacmPumpCspCapa',
          'suarCspCapa',
          'rmk',
          'dataInsUserNm',
          'dataInsDtm',
        ]}
      />
      <GlobalBtnGroup>
        {hasAuth && (
          <Button css={IconButton.button} className="save" onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
      {isUtMatrixRegistHistoryModalOpen && (
        <UtMatrixRegistHistoryPoupup
          open={isUtMatrixRegistHistoryModalOpen}
          close={() => setUtMatrixRegistHistoryModalOpen(false)}
          // condition={{
          //   prdnProcCd: condition?.prdnProcCd,
          //   eqclId: condition?.eqclId,
          //   stndEqpId: utMatrixRegistHistoryCondition?.stndEqpId,
          // }}
        />
      )}
      {isUtMatrixEumTagIdPopUpOpen && (
        <UtMatrixEumTagIdPopup
          open={isUtMatrixEumTagIdPopUpOpen}
          close={() => {
            setUtMatrixEumTagIdPopUpOpen(false);
          }}
          onCallback={(result) => {
            btnSearch();
          }}
        />
      )}
    </ContainerLayout>
  );
};

export default UtMatrixStandardCodePage;
